if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.selectors = this._buildSelectors();
        this._boundDocumentPointerDown = this._onDocumentPointerDown.bind(this);
        this._boundKeyDown = this._onKeyDown.bind(this);
        this._ignoreButtonClick = null;
        this._bindEvents();
        this._setupScrollThumbs();
      }

      // Touch screens: a thin scroll indicator (the native iOS one can't be styled).
      _setupScrollThumbs() {
        if (!window.matchMedia('(hover: none) and (pointer: coarse)').matches) return;

        this.selectors.forEach(({ panel }) => {
          const field = panel.parentElement;
          if (!field) return;

          const thumb = document.createElement('div');
          thumb.setAttribute('aria-hidden', 'true');
          thumb.style.cssText =
            'position:absolute;right:4px;width:5px;border-radius:2.5px;pointer-events:none;' +
            'background-color:var(--color-gray-500);z-index:30;display:none;opacity:0;' +
            'transition:opacity 0.4s ease;';
          field.appendChild(thumb);

          // iOS draws the list's top border thinner while it scrolls (the scrolling content
          // overlaps it). A fixed frame on top keeps the border a crisp 1px all round.
          const frame = document.createElement('div');
          frame.setAttribute('aria-hidden', 'true');
          frame.style.cssText =
            'position:absolute;left:0;right:0;pointer-events:none;z-index:25;display:none;' +
            'border:1px solid var(--color-gray-600);';
          field.appendChild(frame);

          const update = () => {
            if (panel.hidden) {
              frame.style.display = 'none';
            } else {
              frame.style.display = 'block';
              frame.style.top = `${panel.offsetTop}px`;
              frame.style.height = `${panel.offsetHeight}px`;
            }

            const scrollable = panel.scrollHeight - panel.clientHeight;
            if (panel.hidden || scrollable <= 1) {
              thumb.style.display = 'none';
              return;
            }
            const pad = 4;
            const track = panel.clientHeight - pad * 2;
            const height = Math.max(40, (track * panel.clientHeight) / panel.scrollHeight);
            const progress = Math.min(Math.max(panel.scrollTop / scrollable, 0), 1);
            thumb.style.display = 'block';
            thumb.style.height = `${height}px`;
            thumb.style.top = `${panel.offsetTop + panel.clientTop + pad + progress * (track - height)}px`;
          };

          // Like iOS: only visible while scrolling.
          let fadeTimer;
          const reveal = () => {
            update();
            thumb.style.transition = 'opacity 0.1s ease';
            thumb.style.opacity = '0.6';
            clearTimeout(fadeTimer);
            fadeTimer = setTimeout(() => {
              thumb.style.transition = 'opacity 0.4s ease';
              thumb.style.opacity = '0';
            }, 700);
          };

          // Opening the list jumps it to the selected country; that scroll shouldn't show it.
          let ignoreScrollUntil = 0;
          panel.addEventListener(
            'scroll',
            () => (performance.now() < ignoreScrollUntil ? update() : reveal()),
            { passive: true }
          );
          new MutationObserver((mutations) => {
            const opened = mutations.some(
              (m) => m.target === panel && m.attributeName === 'hidden' && !panel.hidden
            );
            update();
            if (opened) {
              // Let the frame follow any open animation of the list (fade/slide in)
              panel.getAnimations?.().forEach((anim) => {
                const effect = anim.effect;
                if (effect?.getKeyframes) frame.animate(effect.getKeyframes(), effect.getTiming());
              });
              ignoreScrollUntil = performance.now() + 150;
              clearTimeout(fadeTimer);
              thumb.style.transition = 'none';
              thumb.style.opacity = '0';
            }
            requestAnimationFrame(update);
          }).observe(panel, {
            attributes: true,
            attributeFilter: ['hidden', 'class'],
            subtree: true,
            childList: true,
          });
          if ('ResizeObserver' in window) new ResizeObserver(update).observe(panel);
        });
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this._boundDocumentPointerDown, true);
      }

      _buildSelectors() {
        const buttons = this.querySelectorAll('[data-button]');
        const selectors = [];
        buttons.forEach((button) => {
          const container =
            button.closest('[data-localization-field]') || button.closest('.relative') || button.parentElement;
          const panel = container?.querySelector('[data-panel]');
          const inputName = button.dataset.inputName || panel?.dataset.inputName;
          if (!panel || !inputName || !this.form) return;
          const input = this.form.querySelector(`input[name="${inputName}"]`);
          if (!input) return;
          selectors.push({ button, panel, input, inputName, container });
        });
        return selectors;
      }

      _bindEvents() {
        // Capture phase so ArrowDown/ArrowUp reach us before <input type="search"> consumes them
        // (otherwise the first key often does nothing visible and it feels like two presses are needed).
        this.addEventListener('keydown', this._boundKeyDown, true);
        this.addEventListener('keyup', this._onKeyUp.bind(this));
        this.addEventListener('focusout', this._onFocusOut.bind(this));
        this.addEventListener('input', this._onCountryFilterInput.bind(this));
        this.addEventListener('click', this._onClearCountrySearchClick.bind(this));

        this.selectors.forEach(({ button }) => {
          button.addEventListener('pointerdown', (e) => this._onButtonPointerDown(button, e));
          button.addEventListener('click', (e) => this._onButtonClick(button, e));
        });

        this.querySelectorAll('[data-panel] a[data-value]').forEach((link) => {
          link.addEventListener('click', this._onItemClick.bind(this));
        });

        // Mobile: taps often don't move focus, so focusout never fires. Pointer events do.
        document.addEventListener('pointerdown', this._boundDocumentPointerDown, true);
      }

      _onDocumentPointerDown(event) {
        const open = this._getOpenSelector();
        if (!open) return;
        const root = open.container || open.button;
        if (root.contains(event.target)) return;
        this._hideAllPanels();
      }

      _getSelectorForButton(button) {
        return this.selectors.find((s) => s.button === button);
      }

      _getSelectorForPanel(panel) {
        return this.selectors.find((s) => s.panel === panel);
      }

      _getOpenSelector() {
        return this.selectors.find((s) => !s.panel.hidden);
      }

      _isSelectorOpen(selector) {
        return selector.button.getAttribute('aria-expanded') === 'true' || !selector.panel.hidden;
      }

      _hidePanel(selector) {
        if (!selector) return;
        selector.button.setAttribute('aria-expanded', 'false');
        selector.panel.setAttribute('hidden', '');
        if (selector.inputName === 'country_code') {
          selector.container.querySelector('[data-country-search-overlay]')?.setAttribute('hidden', '');
          const search = selector.container.querySelector('[data-country-filter]');
          if (search) {
            search.blur();
            search.value = '';
            this._filterCountryList(selector.panel, '');
          }
        }
      }

      _onButtonPointerDown(button, event) {
        const selector = this._getSelectorForButton(button);
        if (!selector || !this._isSelectorOpen(selector)) return;

        event.preventDefault();
        this._hidePanel(selector);
        this._ignoreButtonClick = button;
      }

      _onButtonClick(button, event) {
        if (this._ignoreButtonClick === button) {
          this._ignoreButtonClick = null;
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        this._openSelector(button);
      }

      _hideAllPanels() {
        this.selectors.forEach((s) => this._hidePanel(s));
      }

      _openSelector(button) {
        const selector = this._getSelectorForButton(button);
        if (!selector) return;

        const wasOpen = this._isSelectorOpen(selector);

        this._hideAllPanels();

        if (!wasOpen) {
          selector.panel.removeAttribute('hidden');
          selector.button.setAttribute('aria-expanded', 'true');
          if (selector.inputName === 'country_code') {
            selector.container.querySelector('[data-country-search-overlay]')?.removeAttribute('hidden');
            const search = selector.container.querySelector('[data-country-filter]');
            if (search) {
              search.value = '';
              this._filterCountryList(selector.panel, '');
              this._scrollToCurrentCountry(selector.panel);
              // On touch screens, don't open the keyboard right away: it would cover the
              // list. Tapping the field again focuses the search input to type a country.
              const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
              if (!isTouch) {
                search.focus({ preventScroll: true });
                if (document.activeElement !== search) {
                  requestAnimationFrame(() => search.focus({ preventScroll: true }));
                }
              }
            }
          } else if (selector.inputName === 'locale_code') {
            requestAnimationFrame(() => this._focusInitialLocaleOption(selector));
          }
        }
      }

      // Open the list scrolled so the selected country is the first visible option.
      _scrollToCurrentCountry(panel) {
        const current = panel.querySelector('a[aria-current="true"]')?.closest('li');
        panel.scrollTop = current
          ? current.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop - panel.clientTop
          : 0;
      }

      _focusInitialLocaleOption(selector) {
        const links = this._visibleOptionLinks(selector.panel);
        const cur = links.find((a) => a.getAttribute('aria-current') === 'true');
        const link = cur || links[0];
        this._focusOptionLink(link);
      }

      _visibleOptionLinks(panel) {
        return Array.from(panel.querySelectorAll('li:not(.hidden) a[data-value]'));
      }

      _focusOptionLink(link) {
        if (!link) return;
        link.focus();
        link.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      _ariaCurrentIndex(links) {
        const i = links.findIndex((a) => a.getAttribute('aria-current') === 'true');
        return i >= 0 ? i : -1;
      }

      _onCountryFilterInput(event) {
        const input = event.target;
        if (!input.matches?.('[data-country-filter]')) return;
        const selector = this.selectors.find(
          (s) => s.inputName === 'country_code' && s.container?.contains(input)
        );
        if (!selector) return;
        this._filterCountryList(selector.panel, input.value);
      }

      _onClearCountrySearchClick(event) {
        const btn = event.target.closest('[data-clear-search]');
        if (!btn || !this.contains(btn)) return;
        const field = btn.closest('[data-localization-field]');
        if (!field) return;
        const input = field.querySelector('[data-country-filter]');
        if (!input) return;
        event.preventDefault();
        event.stopPropagation();
        input.value = '';
        const selector = this.selectors.find(
          (s) => s.inputName === 'country_code' && s.container === field
        );
        if (selector) this._filterCountryList(selector.panel, '');
        input.focus();
      }

      _filterCountryList(panel, query) {
        const q = (query || '').trim().toLowerCase();
        const list = panel.querySelector('ul');
        if (!list) return;
        list.querySelectorAll('li').forEach((li) => {
          const link = li.querySelector('a[data-value]');
          const name = link?.querySelector('span')?.textContent?.trim().toLowerCase() || '';
          const iso = (link?.dataset.value || '').toLowerCase();
          const match = !q || name.includes(q) || iso.includes(q);
          li.classList.toggle('hidden', !match);
        });

        const emptyEl = panel.querySelector('[data-country-filter-empty]');
        if (emptyEl) {
          const anyVisible = Array.from(list.querySelectorAll('li')).some((li) => !li.classList.contains('hidden'));
          emptyEl.toggleAttribute('hidden', anyVisible || !q);
        }
      }

      _onItemClick(event) {
        event.preventDefault();
        const link = event.currentTarget;
        const panel = link.closest('[data-panel]');
        const selector = this._getSelectorForPanel(panel);
        if (!selector) return;

        selector.input.value = link.dataset.value || '';

        const buttonDisplay = selector.button.querySelector('.flex.flex-row.flex-nowrap');
        if (buttonDisplay) {
          buttonDisplay.innerHTML = link.innerHTML;
        }

        const list = panel.querySelector('ul');
        if (list) {
          list.querySelectorAll('[aria-current]').forEach((el) => el.removeAttribute('aria-current'));
          list.querySelectorAll('a').forEach((a) => {
            const li = a.closest('li');
            if (a === link) {
              a.setAttribute('aria-current', 'true');
              if (li) {
                li.querySelector('a')?.classList.remove('bg-gray-100', 'bg-gray-200', 'focus-visible:bg-gray-200');
                li.querySelector('a')?.classList.add('bg-gray-300', 'hover:bg-gray-300', 'focus-visible:bg-gray-300');
              }
            } else {
              if (li) {
                li.querySelector('a')?.classList.remove('bg-gray-300', 'hover:bg-gray-300', 'focus-visible:bg-gray-300');
                li.querySelector('a')?.classList.add('bg-gray-100', 'hover:bg-gray-200', 'focus-visible:bg-gray-200');
              }
            }
          });
        }

        if (selector.inputName === 'country_code') {
          this._updateLanguageOptions(link.dataset.languages);
        }

        this._hidePanel(selector);
      }

      _updateLanguageOptions(languagesJson) {
        let languages;
        try {
          languages = JSON.parse(languagesJson || '[]');
        } catch {
          return;
        }
        if (!Array.isArray(languages) || languages.length === 0) return;

        const localeSelector = this.selectors.find((s) => s.inputName === 'locale_code');
        if (!localeSelector) return;

        const panel = localeSelector.panel;
        const list = panel.querySelector('ul');
        if (!list) return;

        const currentLocale = localeSelector.input.value;
        const currentInList = languages.some((lang) => lang.code === currentLocale);
        const localeToSet = currentInList ? currentLocale : languages[0].code;

        list.innerHTML = languages
          .map(
            (lang) => `
            <li class="relative flex items-center" tabindex="-1">
              <a
                href="#"
                tabindex="-1"
                class="flex flex-row flex-nowrap gap-4 justify-start items-center p-4 w-full ${lang.code === localeToSet ? 'bg-gray-300 hover:bg-gray-300 focus-visible:bg-gray-300' : 'bg-gray-100 hover:bg-gray-200 focus-visible:bg-gray-200'}"
                data-value="${this._escapeHtml(lang.code)}"
                ${lang.code === localeToSet ? 'aria-current="true"' : ''}
              >
                ${this._getFlagHtml(lang.code)}
                <span>${this._escapeHtml(lang.name)}</span>
              </a>
            </li>
          `
          )
          .join('');

        localeSelector.input.value = localeToSet;

        const buttonDisplay = localeSelector.button.querySelector('.flex.flex-row.flex-nowrap');
        if (buttonDisplay) {
          const selected = languages.find((l) => l.code === localeToSet);
          buttonDisplay.innerHTML = selected
            ? `${this._getFlagHtml(selected.code)}<span>${this._escapeHtml(selected.name)}</span>`
            : buttonDisplay.innerHTML;
        }

        panel.querySelectorAll('a[data-value]').forEach((link) => {
          link.addEventListener('click', this._onItemClick.bind(this));
        });
      }

      _getFlagHtml(localeCode) {
        if (typeof window.flags !== 'object' || !localeCode) return '';
        const key = localeCode.toUpperCase();
        const svg = window.flags[key];
        return typeof svg === 'string' ? svg.trim() : '';
      }

      _escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }

      _onFocusOut(event) {
        const open = this._getOpenSelector();
        if (!open) return;
        const rt = event.relatedTarget;
        const root = open.container || open.button;
        // Mobile taps on the trigger often blur search with relatedTarget null; closing here
        // runs _hidePanel before the button click, so the next click reopens (toggle feels broken).
        if (!rt) return;
        if (root.contains(rt)) return;
        this._hideAllPanels();
      }

      _onKeyDown(event) {
        const openSelector = this._getOpenSelector();
        if (!openSelector) {
          if (event.code === 'Space' && this.selectors.some((s) => s.button === document.activeElement)) {
            event.preventDefault();
            this._openSelector(document.activeElement);
          }
          return;
        }

        const active = document.activeElement;
        const root = openSelector.container || openSelector.button;
        if (!root.contains(active)) return;

        const focusableLinks = this._visibleOptionLinks(openSelector.panel);
        if (focusableLinks.length === 0) return;

        const focusedIndex = focusableLinks.indexOf(active);
        const fromCountryFilter =
          openSelector.inputName === 'country_code' && active.matches?.('[data-country-filter]');

        const moveFromOutsideList = () => {
          let idx;
          if (fromCountryFilter) {
            idx = event.code === 'ArrowUp' ? focusableLinks.length - 1 : 0;
          } else {
            const cur = this._ariaCurrentIndex(focusableLinks);
            if (event.code === 'ArrowDown') {
              idx = cur >= 0 ? cur : 0;
            } else {
              idx = cur >= 0 ? cur : focusableLinks.length - 1;
            }
          }
          this._focusOptionLink(focusableLinks[idx]);
        };

        switch (event.code) {
          case 'ArrowDown':
            event.preventDefault();
            if (focusedIndex < 0) {
              moveFromOutsideList();
            } else if (focusedIndex < focusableLinks.length - 1) {
              this._focusOptionLink(focusableLinks[focusedIndex + 1]);
            } else {
              this._focusOptionLink(focusableLinks[0]);
            }
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (focusedIndex < 0) {
              moveFromOutsideList();
            } else if (focusedIndex > 0) {
              this._focusOptionLink(focusableLinks[focusedIndex - 1]);
            } else {
              this._focusOptionLink(focusableLinks[focusableLinks.length - 1]);
            }
            break;
          case 'Home':
            event.preventDefault();
            this._focusOptionLink(focusableLinks[0]);
            break;
          case 'End':
            event.preventDefault();
            this._focusOptionLink(focusableLinks[focusableLinks.length - 1]);
            break;
        }
      }

      _onKeyUp(event) {
        const openSelector = this._getOpenSelector();

        if (event.code === 'Escape' && openSelector) {
          this._hidePanel(openSelector);
          openSelector.button.focus();
          event.stopPropagation();
        }
      }
    }
  );
}
